import config from "appConfig";

const theory = `<h3 class=sectionHead>Solutions for the acoustic pressure</h3>
<p>In this chapter we find the solutions of the 1D wave equation and interpret these solutions as a superposition of progressive waves.
<p>
   If we examine the wave equation in which the variable
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
   </math>
   is replaced by the variable
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mi>t</mi>
   </math>
   (which leads to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>c</mi>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>c</mi>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>c</mi>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>), we note that the second derivative with respect to rescaled time (variable
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mi>t</mi>
   </math>) is the same as the second derivative with respect to space (variable
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>). This suggests a great similarity between time dependence and spatial dependence of the solution. Thus, if we write that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
   </math>
   is a continuous and differentiable function of a single variable, we verify that the derivative of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   with respect to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is equal to the derivative of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   with respect to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mi>t</mi>
   </math>. In our case, only the second derivatives have to be equal, which gives another possible solution
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. All linear combinations of these two solutions are also solutions of the wave equation and we can show that all oscillatory solutions (zero mean over time) of the wave equation can be written in the form
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>c</mi>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>f</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>c</mi>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>c</mi>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
   </math>
   are continuous and differentiable function of a single variable (in practice this will not be true when the Dirac “function” or step function will be used).
<h4 class=subsectionHead>Physical interpretation of the solutions</h4>
<p>
   Consider the solution
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   shown in <a href=# data-img-src=${config.ASSETS_URL}/01/03/theory/figure-1.svg data-img-alt="Figure 1. Illustration of a progressive wave <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>p</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-punc>,</mo><mi>t</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mi>f</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-bin>−</mo><mi>c</mi><mi>t</mi><mo class=MathClass-close>)</mo></math>. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>t</mi></mrow><mrow><mn>1</mn></mrow></msub></math> is the time needed by the wave to move from 0 to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>x</mi></mrow><mrow><mn>1</mn></mrow></msub></math> with a speed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>c</mi></math>.">Figure 1</a>. At time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>. At time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>c</mi>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>c</mi>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>. The entire function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   is then moved a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   to the right in a time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>. Thus, we can say that the function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   moves to the right with velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>, which explains <span class=cmti-10>a posteriori </span>the notation choice
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>γ</mi>
            <msub>
               <mi>P</mi>
               <mi>atm</mi>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. It is important not to confuse the sound velocity with the particle velocity, which is the velocity related to the displacement of the particles induced by the passage of the wave.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/03/theory/figure-1.svg alt="Figure 1. Illustration of a progressive wave <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>p</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-punc>,</mo><mi>t</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mi>f</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-bin>−</mo><mi>c</mi><mi>t</mi><mo class=MathClass-close>)</mo></math>. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>t</mi></mrow><mrow><mn>1</mn></mrow></msub></math> is the time needed by the wave to move from 0 to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>x</mi></mrow><mrow><mn>1</mn></mrow></msub></math> with a speed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>c</mi></math>."><figcaption class=caption>Figure 1. Illustration of a progressive wave <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>p</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-punc>,</mo><mi>t</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mi>f</mi><mo class=MathClass-open>(</mo><mi>x</mi><mo class=MathClass-bin>−</mo><mi>c</mi><mi>t</mi><mo class=MathClass-close>)</mo></math>. <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>t</mi></mrow><mrow><mn>1</mn></mrow></msub></math> is the time needed by the wave to move from 0 to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>x</mi></mrow><mrow><mn>1</mn></mrow></msub></math> with a speed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>c</mi></math>.</figcaption></figure>
<p>
   This solution for the function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   which moves while the medium is not moving (the average speed is zero) is called a traveling or progressive wave. This solution is sometimes noted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
<p>
   Considering the function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , a similar consideration shows that in this case the wave moves to the left at a speed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   . This solution is sometimes noted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
<p>In 1D there are only two possible solutions, because on an axis there are only two opposite directions. In two or three dimensions there is an infinity of possible directions and the sum of the possible solutions is then an integral equation (continuous sum).
<p>
   In one dimension we can always write
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   are respectively two waves progressing in the direction of increasing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   and in the direction of decreasing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   . These two progressive waves are sometimes called out-going wave (which comes from the source) and in-going wave (which returns to the source)
<h3 class=sectionHead>Solutions for particle velocity</h3>
<p>
   Since the velocity obeys the wave equation, it can be written as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>, but this does not reflect the relationship between pressure and velocity. We therefore calculate the velocity from the pressure using the Euler equation
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Knowing that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>c</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   [Eq. (1)], the Euler equation reads
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>c</mi>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>g</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>c</mi>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>g</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   are the respective derivatives of functions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
   </math>. Integrating with respect to time we obtain finally
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>v</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mi>f</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>c</mi>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>g</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>c</mi>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   So, if we consider the two waves separately, we have:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>.
<p>
   The particle velocity for a progressive plane wave is therefore simply obtained by dividing the pressure by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   for a wave moving towards the right and by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   for a wave moving towards the left. This value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   represents the modulus of the acoustic impedance (pressure/speed ratio) of the progressive waves. This is a characteristic of the medium, since it depends only on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   . This impedance is called the characteristic impedance. It is important to note (and remember) the sign difference between the impedance of a wave moving towards the right and that of a wave moving towards the left. This difference is easily understood if we remember the scalar character of the pressure and the vectorial character of the particle velocity. A wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   can be seen as a reversal (mirror-like symmetry) of a wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   . Now, in a mirror-like symmetry, the image of the pressure is the pressure itself whereas the image the velocity is the opposite.
<p>In summary, the solutions of the plane wave can be written
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow class=boxed>
               <mrow>
                  <mo fence=true form=prefix>{</mo>
                  <mrow>
                     <mtable class=aligned>
                        <mtr>
                           <mtd columnalign=right>
                              <mi>p</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>x</mi>
                              <mo class=MathClass-punc>,</mo>
                              <mi>t</mi>
                              <mo class=MathClass-close>)</mo>
                           </mtd>
                           <mtd columnalign=left>
                              <mo class=MathClass-rel>=</mo>
                              <mi>f</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>x</mi>
                              <mo class=MathClass-bin>−</mo>
                              <mi>c</mi>
                              <mi>t</mi>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-bin>+</mo>
                              <mi>g</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>x</mi>
                              <mo class=MathClass-bin>+</mo>
                              <mi>c</mi>
                              <mi>t</mi>
                              <mo class=MathClass-close>)</mo>
                           </mtd>
                        </mtr>
                        <mtr>
                           <mtd columnalign=right>
                              <mi>v</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>x</mi>
                              <mo class=MathClass-punc>,</mo>
                              <mi>t</mi>
                              <mo class=MathClass-close>)</mo>
                           </mtd>
                           <mtd columnalign=left>
                              <mo class=MathClass-rel>=</mo>
                              <mfrac>
                                 <mrow>
                                    <mi>f</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <mi>x</mi>
                                    <mo class=MathClass-bin>−</mo>
                                    <mi>c</mi>
                                    <mi>t</mi>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-bin>−</mo>
                                    <mi>g</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <mi>x</mi>
                                    <mo class=MathClass-bin>+</mo>
                                    <mi>c</mi>
                                    <mi>t</mi>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <msub>
                                       <mrow>
                                          <mi>ρ</mi>
                                       </mrow>
                                       <mrow>
                                          <mn>0</mn>
                                       </mrow>
                                    </msub>
                                    <mi>c</mi>
                                 </mrow>
                              </mfrac>
                           </mtd>
                        </mtr>
                     </mtable>
                  </mrow>
               </mrow>
            </mrow>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(5)
</table>
<h3 class=sectionHead>Note on the speed of sound</h3>
<p>We have seen that the speed of sound is given by the formula
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>c</mi>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>γ</mi>
                        <msub>
                           <mi>P</mi>
                           <mi>atm</mi>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(6)
</table>
This formula might suggest that the speed of sound depends on the pressure. In fact, it is not because the pressure and the density vary proportionally. For a perfect gas, we have indeed
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>n</mi>
   <mi>R</mi>
   <mi>T</mi>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mi>M</mi>
      </mrow>
   </mfrac>
   <mi>R</mi>
   <mi>T</mi>
</math>
, where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>m</mi>
</math>
is the mass of an ideal gas and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>M</mi>
</math>
is the molar mass (the mass of one mole). As
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mi>V</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
, it can be written that
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mi>γ</mi>
         <msub>
            <mi>P</mi>
            <mi>atm</mi>
         </msub>
      </mrow>
      <mrow>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mn>1</mn>
      </mrow>
      <mrow>
         <mi>M</mi>
      </mrow>
   </mfrac>
   <mi>R</mi>
   <mi>T</mi>
</math>
and then
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>c</mi>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>γ</mi>
                        <mi>R</mi>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The speed of sound therefore depends on the temperature and the molar mass of the gas. In the case of air, the molar mass is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mn>9</mn>
   </math> g, which leads to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mn>4</mn>
      <mn>3</mn>
   </math>  m/s at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
   (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>γ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>4</mn>
      <mn>0</mn>
      <mn>2</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>8</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>3</mn>
      <mn>1</mn>
      <mn>4</mn>
    </math>   
      <msup>
 J.mol <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
          <msup><mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
 </math>K<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup><mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   ).
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
   <dt>[2]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[3]
   <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
